import { translate } from 'containers/services/translate';
import { injectIntl, IntlShape } from 'react-intl';
import SahamDrawer from 'components/SahamDrawer';
import styled from 'styled-components';
import { Typography } from '@material-ui/core';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import RegulationHistoryTimeline from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RegulationHistoryTimeline';
import RegulationValidationDialog from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RegulationValidationDialog';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  PROCEDURE_STATUSES,
  TIMELINE_REGULATION_STATUS,
} from 'containers/data-structure';
import { isEmpty, get } from 'lodash';
import currentUserStorage from 'containers/services/security/current-user-storage';
import userPrivileges from 'containers/services/security/user-privileges';
import {
  BNEJ_CITIES,
  REGULATION_STATUSES_CODES,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import {
  getAllDecidedBy,
  isTransferBNEJActivated,
  transferPayment,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';

export interface RegulationValidationDrawerProps {
  intl: IntlShape;
  open: boolean;
  handleToggleDrawer: Function;
  requestValidateRegulation: Function;
  requestGetRegulation: Function;
  regulationId: number;
  corpoRegulationId: number;
  claimNumber: string;
  history: any;
  claimId: number;
  isConsultation: boolean;
  isRegulationPending: boolean;
  listings: any;
  regulationRejectionInfo: any;
  transactionTimelineId?: any;
  transactionId?: any;
  victimId?: any;
}

const RegulationValidationDrawer: React.FC<RegulationValidationDrawerProps> = (
  props,
): JSX.Element => {
  const [isOpenValidationDialog, setIsOpenValidationDialog] = useState<boolean>(
    false,
  );
  const [isValidation, setIsValidation] = useState<boolean>(false);
  const userInboxes = useMemo(() => {
    return userPrivileges
      .getUserInboxes()
      .map((el: any) => el.managerInboxesId);
  }, []);
  const currentUser = currentUserStorage?.get().username;

  const pendingRegulation = props.history.find(
    (item: any) => item.status === TIMELINE_REGULATION_STATUS.PENDING,
  );

  const Title = styled(Typography)`
    text-align: left;
    font-weight: 500;
    font-size: small;
    padding: 2px 20px;
  `;
  const RegulationValidationLawyerTitle = styled.div`
    display: flex;
    justify-content: space-between;
  `;

  const successCallBack = () => {
    isValidation
      ? toast.success(
          translate(messages.RegulationVaidationDialogSuccess, props),
        )
      : toast.success(
          translate(messages.RegulationRejectionDialogSuccess, props),
        );
    setIsOpenValidationDialog(false);
  };

  const handleRegulationValidation = (
    comment: string,
    rejectionReasonId: number,
    rejectionType: string,
  ) => () => {
    const isLastValidationLevel =
      pendingRegulation && pendingRegulation?.isLast;
    props.requestValidateRegulation(
      {
        id: props.corpoRegulationId,
        victimId: props.victimId,
        claimId: Number(props.claimId),
        claimNumber: props.claimNumber,
        comment,
        status: isValidation
          ? PROCEDURE_STATUSES.VALIDATED
          : PROCEDURE_STATUSES.REJECTED,
        validator: currentUser,
        isLast: isLastValidationLevel,
        paymentId: props.regulationId,
        rejectionReasonId: rejectionReasonId,
        rejectionType: rejectionType,
        validators: isLastValidationLevel
          ? getAllDecidedBy(props.history, currentUser)
          : [],
        paymentStatusCode: !isValidation
          ? REGULATION_STATUSES_CODES.CANCELED.key
          : REGULATION_STATUSES_CODES.PAID.key,
        isTrackingActive: featureService.hasFeature(
          FEATURES.TRACKING_ACTIONS_USER,
        ),
        isTransaction:
          props.transactionId &&
          featureService.hasFeature(FEATURES.TRANSACTION),
        transactionTimelineId: props.transactionTimelineId,
        transactionId: props.transactionId,
        isTransferActivated:
          isTransferBNEJActivated(get(props.listings, 'appConfig', [])) &&
          String(pendingRegulation?.regulation?.paymentMethod) ===
            String(transferPayment(get(props.listings, 'PaymentMethod', []))) &&
          Object.values(BNEJ_CITIES).includes(
            pendingRegulation?.regulation.recipient,
          ),
      },
      successCallBack,
    );
  };

  const shouldDisableDrawerActions = () => {
    if (
      isEmpty(props.history) ||
      props.isConsultation ||
      !props.isRegulationPending
    ) {
      return true;
    }

    return (
      props.history.some(
        (item: any) => item.status === TIMELINE_REGULATION_STATUS.REJECTED,
      ) ||
      props.history.every(
        (item: any) => item.status === TIMELINE_REGULATION_STATUS.VALIDATED,
      ) ||
      currentUser === pendingRegulation.regulation.creator ||
      !userInboxes.includes(pendingRegulation.managerInbox.id)
    );
  };

  const getDisablingReasons = () => [
    {
      condition: props.isConsultation,
      reason: translate(messages.consultationMode, props),
    },
    {
      condition: !props.isRegulationPending,
      reason: translate(messages.regulationNotPending, props),
    },
    {
      condition: currentUser === pendingRegulation?.regulation?.creator,
      reason: translate(messages.currentUserIsCreator, props),
    },
    {
      condition:
        props.isRegulationPending &&
        !userInboxes.includes(pendingRegulation?.managerInbox?.id),
      reason: translate(messages.noAccessToManagerInbox, props),
    },
  ];

  const disablingReason = useMemo(() => {
    if (isEmpty(props.history)) {
      return [translate(messages.historyEmpty, props)];
    } else {
      return getDisablingReasons()
        .filter(reasonObj => reasonObj.condition)
        .map(reasonObj => reasonObj.reason);
    }

    // eslint-disable-next-line
  }, [
    props.history,
    props.isConsultation,
    props.isRegulationPending,
    userInboxes,
    currentUser,
    pendingRegulation,
  ]);

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={props.handleToggleDrawer}
      title={translate({ ...messages.validationRegulationDrawerTitle }, props)}
      confirmButtonTitle={translate(
        { ...messages.validationRegulationDrawerButtonTitle },
        props,
      )}
      isRejectButtonDisabled={shouldDisableDrawerActions()}
      isSaveButtonDisabled={shouldDisableDrawerActions()}
      handleValidateDrawer={() => {
        setIsValidation(true);
        setIsOpenValidationDialog(true);
      }}
      rejectButtonTitle={translate(
        { ...messages.rejectionRegulationDrawerButtonTitle },
        props,
      )}
      handleRejectDrawer={() => {
        setIsValidation(false);
        setIsOpenValidationDialog(true);
      }}
      disableReason={disablingReason}
    >
      <div>
        <RegulationValidationLawyerTitle>
          <Title gutterBottom style={{ fontSize: '18px' }}></Title>
        </RegulationValidationLawyerTitle>
        <RegulationHistoryTimeline
          history={props.history}
          regulationRejectionInfo={props.regulationRejectionInfo}
        />
      </div>
      <RegulationValidationDialog
        open={isOpenValidationDialog}
        onClose={() => setIsOpenValidationDialog(false)}
        handleValidation={handleRegulationValidation}
        isValidation={isValidation}
        listings={props.listings}
      />
    </SahamDrawer>
  );
};
export default injectIntl(RegulationValidationDrawer);
